import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useOrder } from '@/hooks/useOrders'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { OrderStatusTracker } from '@/components/account/order-status-tracker'
import { ArrowLeft, Package, Loader2, ExternalLink } from 'lucide-react'
import { format } from 'date-fns'
import type { FulfillmentStatus, ShippingAddress, OrderItem } from '@/types/database'
import { BRAND_NAME } from '@/lib/branding'

const statusColors: Record<FulfillmentStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
}

function parseCustomInputValue(value: string): { url: string; fileName: string } | null {
  if (value.includes('||')) {
    const [url, fileName] = value.split('||')
    return { url, fileName }
  }
  return null
}

function OrderItemRow({ item }: { item: OrderItem }) {
  const customInputs = item.custom_inputs as Record<string, string> | null

  return (
    <div className="flex gap-4 py-4 border-b last:border-b-0">
      <div className="h-16 w-16 rounded-md bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
        {item.product_thumbnail_url ? (
          <img
            src={item.product_thumbnail_url}
            alt={item.product_title || 'Product'}
            className="h-full w-full object-cover"
          />
        ) : (
          <Package className="h-6 w-6 text-muted-foreground" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-medium">{item.product_title || 'Product'}</p>
        {item.variant_label && (
          <Badge variant="secondary" className="mt-1 text-xs">
            {item.variant_label}
          </Badge>
        )}
        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
          <span>Qty: {item.quantity}</span>
          {item.discount_percent > 0 && (
            <span className="text-green-600">-{item.discount_percent}% off</span>
          )}
        </div>

        {/* Custom inputs */}
        {customInputs && Object.keys(customInputs).length > 0 && (
          <div className="mt-2 space-y-1">
            {Object.entries(customInputs).map(([label, value]) => {
              const upload = parseCustomInputValue(value)
              return (
                <div key={label} className="text-xs text-muted-foreground">
                  <span className="font-medium">{label}: </span>
                  {upload ? (
                    <a
                      href={upload.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      {upload.fileName}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span>{value}</span>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="text-right flex-shrink-0">
        <p className="font-medium">${Number(item.line_total).toFixed(2)}</p>
        {item.quantity > 1 && (
          <p className="text-xs text-muted-foreground">
            ${Number(item.unit_price).toFixed(2)} each
          </p>
        )}
      </div>
    </div>
  )
}

export function AccountOrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { order, orderItems, isLoading, error } = useOrder(id)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="max-w-4xl mx-auto">
        <Link to="/account/orders" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Orders
        </Link>
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">Order not found</h3>
          <p className="text-muted-foreground mt-1">
            This order doesn't exist or you don't have permission to view it.
          </p>
        </div>
      </div>
    )
  }

  const shippingAddress = order.shipping_address as ShippingAddress | null
  const hasItems = orderItems.length > 0

  return (
    <>
      <Helmet>
        <title>{`Order #${order.id.slice(0, 8)} | ${BRAND_NAME}`}</title>
      </Helmet>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back Link */}
        <Link to="/account/orders" className="inline-flex items-center text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Orders
        </Link>

        {/* Order Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Order #{order.id.slice(0, 8)}</h1>
            <p className="text-muted-foreground mt-1">
              Placed on {format(new Date(order.created_at), 'MMMM d, yyyy')}
            </p>
          </div>
          <Badge className={statusColors[order.fulfillment_status]}>
            {order.fulfillment_status}
          </Badge>
        </div>

        {/* Status Tracker */}
        <OrderStatusTracker
          status={order.fulfillment_status}
          createdAt={order.created_at}
          shippedAt={order.shipped_at}
          deliveredAt={order.delivered_at}
        />

        {/* Items Ordered */}
        <Card>
          <CardHeader>
            <CardTitle>Items Ordered</CardTitle>
          </CardHeader>
          <CardContent>
            {hasItems ? (
              <div className="divide-y">
                {orderItems.map((item) => (
                  <OrderItemRow key={item.id} item={item} />
                ))}
              </div>
            ) : (
              // Fallback: legacy single-product display
              <div className="flex gap-4">
                <div className="h-20 w-20 rounded-md bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                  {order.product?.video_thumbnail_url ? (
                    <img
                      src={order.product.video_thumbnail_url}
                      alt={order.product.title || 'Product'}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Package className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <p className="font-medium">{order.product?.title || 'Product'}</p>
                  <p className="text-sm text-muted-foreground">Qty: {order.quantity}</p>
                  {order.discount_percent && (
                    <p className="text-sm text-muted-foreground">
                      {order.discount_percent}% discount applied
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Order Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {hasItems ? (
              <>
                {orderItems.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-muted-foreground truncate pr-4">
                      {item.product_title} {item.quantity > 1 ? `x${item.quantity}` : ''}
                    </span>
                    <span className="flex-shrink-0">${Number(item.line_total).toFixed(2)}</span>
                  </div>
                ))}
                {order.total_savings && Number(order.total_savings) > 0 && (
                  <div className="flex justify-between text-sm text-green-600 pt-1">
                    <span>Total Savings</span>
                    <span>-${Number(order.total_savings).toFixed(2)}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                {order.unit_price && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Unit Price</span>
                    <span>${Number(order.unit_price).toFixed(2)}</span>
                  </div>
                )}
                {order.discount_percent && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Discount</span>
                    <span>-{order.discount_percent}%</span>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-between font-medium pt-2 border-t">
              <span>Total</span>
              <span>${Number(order.total_amount).toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Shipping Address */}
          {shippingAddress && (
            <Card>
              <CardHeader>
                <CardTitle>Shipping Address</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium">{shippingAddress.name}</p>
                <p className="text-muted-foreground">{shippingAddress.line1}</p>
                {shippingAddress.line2 && (
                  <p className="text-muted-foreground">{shippingAddress.line2}</p>
                )}
                <p className="text-muted-foreground">
                  {shippingAddress.city}, {shippingAddress.state} {shippingAddress.postal_code}
                </p>
                <p className="text-muted-foreground">{shippingAddress.country}</p>
              </CardContent>
            </Card>
          )}

          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p>{order.customer_email}</p>
              </div>
              {order.customer_name && (
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p>{order.customer_name}</p>
                </div>
              )}
              {order.customer_phone && (
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p>{order.customer_phone}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-4">
          <Button variant="outline" asChild>
            <a href={`mailto:support@itsonenumber.com?subject=Order%20${order.id.slice(0, 8)}`}>
              Contact Support
            </a>
          </Button>
          {order.tracking_url && (
            <Button asChild>
              <a href={order.tracking_url} target="_blank" rel="noopener noreferrer">
                Track Package
              </a>
            </Button>
          )}
          {order.tracking_number && !order.tracking_url && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Tracking:</span>
              <code className="bg-muted px-2 py-1 rounded text-xs">{order.tracking_number}</code>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
